/**
 * Reading and changing CMS roles.
 *
 * The read side is `userRoles/{uid}`, the mirror `functions/src/roles.ts`
 * maintains so a screen like this can list people without paging the Admin SDK.
 * The write side is the `setUserRole` callable, and it has to be: the authority
 * is a Firebase custom claim, only a privileged server can set one, and
 * `firestore.rules` denies every client a write to `userRoles` precisely so that
 * nobody can promote themselves by editing the mirror.
 *
 * So the two sides are deliberately asymmetric. If they ever disagree, the claim
 * is right and the document is stale.
 */

import type { FirebaseApp } from "firebase/app";
import { collection, getDocs, getFirestore, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import { isRole, type Role } from "../roles";

export interface UserRoleRecord {
    uid: string;
    email?: string;
    displayName?: string;
    /** Absent when the mirror holds something that is no longer a valid role. */
    role?: Role;
    updatedAt?: Date;
    updatedBy?: string;
}

export async function fetchUserRoles(firebaseApp: FirebaseApp): Promise<UserRoleRecord[]> {
    const snapshot = await getDocs(collection(getFirestore(firebaseApp), "userRoles"));

    return snapshot.docs
        .map((doc) => {
            const data = doc.data() as Record<string, unknown>;
            const updatedAt = data.updatedAt;
            return {
                uid: doc.id,
                ...(typeof data.email === "string" ? { email: data.email } : {}),
                ...(typeof data.displayName === "string" ? { displayName: data.displayName } : {}),
                ...(isRole(data.role) ? { role: data.role } : {}),
                ...(updatedAt instanceof Timestamp ? { updatedAt: updatedAt.toDate() } : {}),
                ...(typeof data.updatedBy === "string" ? { updatedBy: data.updatedBy } : {})
            } satisfies UserRoleRecord;
        })
        .sort((a, b) => (a.email ?? a.uid).localeCompare(b.email ?? b.uid));
}

export interface SetUserRoleResult {
    uid: string;
    role: Role;
    changed: boolean;
    previousRole?: string | null;
}

export async function setUserRole(
    firebaseApp: FirebaseApp,
    uid: string,
    role: Role
): Promise<SetUserRoleResult> {
    const callable = httpsCallable<{ uid: string; role: Role }, SetUserRoleResult>(
        getFunctions(firebaseApp),
        "setUserRole"
    );
    const response = await callable({ uid, role });
    return response.data;
}

/**
 * A readable reason, out of a `FunctionsError`.
 *
 * The function's refusals are the interesting part of this screen: they are the
 * safety rules, not incidental errors, and a raw "internal error" would hide the
 * one thing an admin needs to read.
 */
export function describeCallError(error: unknown): string {
    const code = (error as { code?: string } | undefined)?.code;
    const message = error instanceof Error ? error.message : String(error);

    if (code === "functions/failed-precondition") return message;
    if (code === "functions/permission-denied") {
        return "Only an admin may assign roles. Your own role may have changed since you signed in.";
    }
    if (code === "functions/not-found") {
        return "That user no longer exists in Firebase Auth. The row below is a stale mirror; it can be ignored.";
    }
    if (code === "functions/unauthenticated") return "Your session expired. Sign in again.";
    if (code === "functions/internal" || code === undefined) {
        return `The role change did not complete: ${message}. Nothing was changed.`;
    }
    return message;
}
